import { signal } from "@preact/signals";
import { useId } from "$store/sdk/useId.ts";
import Modal from "$store/components/ui/Modal.tsx";
import Icon from "$store/components/ui/Icon.tsx";
import type { ImageWidget } from "apps/admin/widgets.ts";
import { Picture, Source } from "apps/website/components/Picture.tsx";

export interface Props {
  image: {
    src: ImageWidget;
    alt?: string;
  };
  label?: string;
  link?: string;
  variant?: "media-banner";
}

const buttonStyleVariant = {
  "media-banner": "container my-24 h-[443px] w-full p-4 block lg:p-0",
};

const bannerStyleVariant = {
  "media-banner": "!opacity-100",
};

const isOpen = signal(false);

export default function Video({ image, label, link, variant }: Props) {
  const modalId = useId();

  return (
    <>
      <label
        class={`relative group cursor-pointer  ${
          variant ? buttonStyleVariant[variant] : ""
        }`}
        htmlFor={modalId}
        aria-label="Abrir o modal do vídeo"
        onClick={() => {
          isOpen.value = true;
        }}
      >
        <Picture class="h-full">
          <Source
            media="(max-width: 767px)"
            src={image.src}
            width={341}
            height={288}
          />

          <Source
            media="(min-width: 768px)"
            src={image.src}
            width={variant ? 1000 : 553}
            height={466}
          />

          <img
            class={`w-full h-full rounded-lg object-cover opacity-20 ease-linear transition-opacity duration-300 group-data-[active=true]:opacity-100 ${
              variant ? bannerStyleVariant[variant] : ""
            }`}
            src={image.src}
            alt={image.alt}
            decoding="async"
            loading="lazy"
          />
        </Picture>

        {link && (
          <Icon
            id="Play"
            class="opacity-80 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 transition-opacity duration-300 lg:group-hover:opacity-100"
            size={82}
          />
        )}

        {label && (
          <span class="max-w-[75%] py-1 pl-8 pr-6 rounded bg-tw-primary-100 text-white text-2xl font-maven font-extrabold flex items-center absolute top-4 -right-2 z-10 lg:pr-3 lg:pl-28 lg:py-2 lg:-right-6 lg:text-3xl">
            {label}
          </span>
        )}
      </label>

      {link && isOpen.value && (
        <Modal
          id={modalId}
          class="max-w-max p-0 rounded-none shadow-none bg-transparent"
        >
          <div class="w-screen max-w-[calc(100%-1rem)] p-4 mx-auto border-8 border-tw-primary-100 bg-white lg:max-w-4xl lg:px-8 lg:py-7">
            <div class="pb-4 mb-6 border-b border-tw-primary-100 flex justify-between items-center">
              <h2 class="text-tw-neutral-500 text-lg sm:text-[1.375rem]">
                Conheça como surgiu a Saddi Center
              </h2>

              <label
                title="Fechar o modal do vídeo"
                aria-label="Fechar o modal do vídeo"
                htmlFor={modalId}
              >
                <Icon id="CloseOutline" size={34} />
              </label>
            </div>

            <iframe
              class="w-full max-w-4xl aspect-video"
              src={link}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </Modal>
      )}
    </>
  );
}
